/**
 * Parses various array string formats into an array of items.
 * Handles: JSON arrays, ClickHouse array_agg format ['a','b'], comma-separated
 * strings, or values that are already arrays.
 */
export function parseArrayString(
  arrayStr: string | undefined | unknown,
): string[] | null {
  if (!arrayStr) return null

  // If already an array, return it
  if (Array.isArray(arrayStr)) {
    return arrayStr.map(String).filter(Boolean)
  }

  // Convert to string if not already
  const strValue = String(arrayStr).trim()
  if (!strValue) return null

  // ClickHouse array_agg format: ['item1','item2'] or similar
  if (strValue.startsWith("[") && strValue.endsWith("]")) {
    return strValue
      .slice(1, -1)
      .split(",")
      .map(item => item.trim().replace(/^['"]|['"]$/g, ""))
      .filter(Boolean)
  }

  // Try to parse as JSON array
  try {
    const arr = JSON.parse(strValue)
    if (Array.isArray(arr)) {
      return arr.map(String).filter(Boolean)
    }
  } catch {
    // Not JSON, continue to fallback
  }

  // Fallback: split by comma
  return strValue
    .split(",")
    .map((item: string) => item.trim())
    .filter(Boolean)
}

export type TPublicPost = {
  /** Human-clickable link to the public post */
  postUrl: string
  /** Iframe-embeddable URL for the post */
  embedUrl: string
  platform: "Instagram" | "Facebook"
  /**
   * Facebook only: the `pageId_postId` the link was built from, so the drawer
   * can ask the API whether the post is published before embedding it.
   */
  objectStoryId?: string
}

/** Facebook embedded-post plugin URL for a post permalink. */
export function getFacebookEmbedUrl(postUrl: string): string {
  return `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(postUrl)}&show_text=false&width=350`
}

/**
 * Builds the public post link + embeddable URL for a creative from data
 * already on the cube row. This is the fallback player for videos Meta
 * won't let us download (whitelisted/collab creator-page ads) and for
 * assets that resolved to a broken still: the ad's organic post is public
 * even when its video source is withheld from the API.
 *
 * - `permalinkUrl` (media_permalink_url) is an IG or FB permalink.
 * - `objectStoryId` is `{pageId}_{postId}`, convertible to an FB post URL.
 */
export function getPublicPost(
  permalinkUrl?: string,
  objectStoryId?: string,
): TPublicPost | null {
  if (permalinkUrl?.includes("instagram.com")) {
    // Normalize: strip hash/query and trailing slash, then append /embed
    const cleanUrl = permalinkUrl.split(/[?#]/)[0].replace(/\/+$/, "")
    return {
      postUrl: cleanUrl,
      embedUrl: `${cleanUrl}/embed`,
      platform: "Instagram",
    }
  }

  let fbPostUrl: string | null = null
  if (permalinkUrl?.includes("facebook.com")) {
    fbPostUrl = permalinkUrl
  } else if (objectStoryId?.includes("_")) {
    const [pageId, postId] = objectStoryId.split("_")
    fbPostUrl = `https://www.facebook.com/${pageId}/posts/${postId}`
  }

  if (fbPostUrl) {
    return {
      postUrl: fbPostUrl,
      embedUrl: getFacebookEmbedUrl(fbPostUrl),
      platform: "Facebook",
      ...(objectStoryId?.includes("_") ? { objectStoryId } : {}),
    }
  }

  return null
}

/**
 * Resolves the public post for a creative report row using only fields that
 * belong to the same ad.
 *
 * - Grouped mode (one row per ad): the row's own `media_permalink_url` /
 *   `object_story_id`.
 * - Aggregated mode (one row per root ad name): only `top_meta_object_story_id`
 *   is per-ad (the highest-spend Meta row, same one the card image and Blast
 *   use). `media_permalink_url` is a MAX across every ad/channel in the group,
 *   so it would point at an unrelated ad's post and is ignored here.
 */
export function getPublicPostForRow(
  row: Record<string, string | undefined>,
  isAggregated: boolean,
): TPublicPost | null {
  return isAggregated
    ? getPublicPost(undefined, row.top_meta_object_story_id)
    : getPublicPost(row.media_permalink_url, row.object_story_id)
}
